import React, { use, useEffect, useState } from "react";
import "../styles/ItemModels.css";
import "../styles/GrnTableStyles.css";
import { deleteData, getData, postData, patchData } from "../api/ApiServices";
import { ApiResponse, GRN } from "../models/NewGrn";
import { SupplierResponse } from "../models/SupplierParts";
import AddGRNForm from "../forms/AddGRNForm";
import { ContributorResponse } from "../models/contributortypes";
import PopupConfirmation from "../view/common/alert/PopupConfirmation";
import { Trash, Edit } from "lucide-react";
import "../styles/grnformvalidation.css";
import { resetFilters, updateFilters } from "../redux/slices/filterSlice";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../redux/slices/store";

const NewGRN: React.FC = () => {
  const [editingGRN, setEditingGRN] = useState<GRN | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [suppliers, setSuppliers] = useState<SupplierResponse[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [parts, setParts] = useState<any>([]);
  const [grnNo, setGrnNo] = useState<string>("");
  const [tableData, setTableData] = useState<any[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);

  const [prefilledData, setPrefilledData] = useState({
    supplierId: "",
    number: "",
    date: "",
    readOnly: false,
    grnType: "",
    invoiceType: "",
    poNo: ""
  });

  const [isReadOnly, setIsReadOnly] = useState(false);

  const [showPopup, setShowPopup] = useState(false);
  const [popupMessage, setPopupMessage] = useState("");
  const [popupType, setPopupType] = useState<
    "success" | "error" | "warning" | "info"
  >("success");
  const [isConfirmation, setIsConfirmation] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<string>("");
  const [initializeGrn, isGrnInitialized] = useState(false);
  const dispatch = useDispatch();
  const permission = useSelector((state: RootState) => state.permissions)


  useEffect(() => {
    dispatch(resetFilters());
    isGrnInitialized(true);
  }, [dispatch]);

  useEffect(() => {
    if (initializeGrn) {
      fetchSuppliers();
      fetchSupplierParts();
    }
  }, []);

  const fetchSuppliers = async () => {
    setIsLoading(true);
    try {
      const response = await getData<ApiResponse<ContributorResponse>>(
        "/contributors",
        { page: 1, limit: 100 }
      );
      if (response?.data?.data && Array.isArray(response.data.data)) {
        const supplierList = response.data.data.filter(
          (contributor) =>
            contributor.type === "SUPPLIER" ||
            contributor.type === "CUSTOMER&SUPPLIER"
        );
        setSuppliers(supplierList);
      } else {
        setSuppliers([]);
      }
    } catch (error) {
      console.error("Error fetching suppliers:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchSupplierParts = async () => {
    setIsLoading(true);
    try {
      const response = await getData<ApiResponse<SupplierResponse>>(
        "/supplierparts",
        { page: 1, limit: 100 }
      );
      if (response?.data?.data && Array.isArray(response.data.data)) {
        setParts(response.data.data);
      } else {
        setParts([]);
      }
    } catch (error) {
      console.error("Error fetching supplier parts:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const formatDateForInput = (dateString: string) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const handleAddOrEdit = async (grn: GRN) => {
    setIsProcessing(true);
    try {
      let response: any;

      if (isEditing && editingGRN?._id) {
        response = await patchData(`/grns`, editingGRN._id, { ...grn });

        setTableData((prevTableData) =>
          prevTableData.map((item) => {
            if (item._id === editingGRN._id) {
              return {
                ...response.data,
                partId: {
                  ...item.partId,
                  unitPrice:
                    response.data.partId?.unitPrice || item.partId?.unitPrice,
                },
              };
            }
            return item;
          })
        );

        setIsEditing(false);
        setEditingGRN(null);

        setPopupMessage("GRN updated successfully");
      } else {
        response = await postData("/grns", grn);
        const newGRN = response.data[0];

        if (!grnNo) {
          setGrnNo(newGRN.grnNo);
        }

        setPrefilledData({
          supplierId: newGRN.supplierId || "",
          number: newGRN.number || "",
          date: formatDateForInput(newGRN.date) || "",
          readOnly: true,
          grnType: newGRN.grnType,
          invoiceType: newGRN.invoiceType || "",
          poNo: newGRN.poNo || ""
        });

        setIsReadOnly(true);

        setTableData((prevTableData) => [...prevTableData, newGRN]);

        setPopupMessage("GRN saved successfully");
      }

      setPopupType("success");
      setIsConfirmation(false);
      setShowPopup(true);
    } catch (error) {
      console.error("Error saving/updating GRN:", error);

      setPopupMessage(
        isEditing
          ? "Error updating GRN. Please try again."
          : "Duplicate Entry.."
      );
      setPopupType("error");
      setIsConfirmation(false);
      setShowPopup(true);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleComplete = () => {
    setTableData([]);
    setGrnNo("");
    setPrefilledData({ supplierId: "", number: "", date: "", readOnly: false, grnType: "", invoiceType: "", poNo: "" });
    setIsReadOnly(false);
    setIsEditing(false);
    setEditingGRN(null);

    setPopupMessage("GRN process completed successfully");
    setPopupType("success");
    setIsConfirmation(false);
    setShowPopup(true);
  };

  const confirmDelete = (id: string) => {
    if (isEditing) {
      setPopupMessage("Please complete the current editing process before deleting any records.");
      setPopupType("warning");
      setIsConfirmation(false);
      setShowPopup(true);
      return;
    }

    setPopupMessage("Are you sure you want to delete this GRN?");
    setPopupType("warning");
    setIsConfirmation(true);
    setItemToDelete(id);
    setShowPopup(true);
  };

  const handleDelete = async () => {
    if (!itemToDelete) return;

    try {
      await deleteData("/grns", itemToDelete);

      setTableData((prev) => prev.filter((item) => item._id !== itemToDelete));
      setPopupMessage("GRN deleted successfully");
      setPopupType("success");
      setIsConfirmation(false);
      setShowPopup(true);
    } catch (error) {
      console.error("Error deleting GRN:", error);

      setPopupMessage("Error deleting GRN. Please try again.");
      setPopupType("error");
      setIsConfirmation(false);
      setShowPopup(true);
    }
  };

  const handleEdit = (grn: GRN) => {
    const grnToEdit = JSON.parse(JSON.stringify(grn));
    console.log(grnToEdit);

    if (grnToEdit.grnDate) {
      grnToEdit.grnDate = formatDateForInput(grnToEdit.grnDate);
    }

    if (grnToEdit.date) {
      grnToEdit.date = formatDateForInput(grnToEdit.date);
    }

    if (
      grnToEdit.partId &&
      typeof grnToEdit.partId === "object" &&
      grnToEdit.partId._id
    ) {
      grnToEdit.partId = grnToEdit.partId._id;
    }

    setEditingGRN(grnToEdit);
    setIsEditing(true);

    setIsReadOnly(true);

    setPrefilledData({
      supplierId: grnToEdit.supplierId || "",
      number: grnToEdit.number || "",
      date: formatDateForInput(grnToEdit.date) || "",
      readOnly: true,
      grnType: grnToEdit.grnType || "",
      invoiceType: grnToEdit.invoiceType || "",
      poNo: grnToEdit.poNo || ""
    });

    setGrnNo(grnToEdit.grnNo || "");

    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleCancel = () => {
    setEditingGRN(null);
    setIsEditing(false);

    setPrefilledData((prev) => ({
      ...prev,
      readOnly: true,
    }));
  };

  return (
    <div className="grn-container">
      <div className="grn-form-wrapper">
        <AddGRNForm
          onSave={handleAddOrEdit}
          onCancel={handleCancel}
          parts={parts}
          suppliers={suppliers}
          isEditing={isEditing}
          editingGRN={editingGRN}
          grnNo={grnNo}
          prefilledData={prefilledData}
          isReadOnly={isReadOnly}
          isProcessing={isProcessing}
        />
      </div>

      <div className="item-models-table-wrapper">
        <table className="item-models-table grn-table">
          <thead>
            <tr>
              <th>GRN No</th>
              <th>Part No</th>
              <th>Part Name</th>
              <th>Model</th>
              <th>Category</th>
              <th>Quantity(UOM)</th>
              <th>Unit Price</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {tableData.length > 0 ? (
              tableData.map((item, index) => {
                const partDetails = item.partId?.part;

                return (
                  <tr key={index}>
                    <td>{item.grnNo}</td>
                    <td>{partDetails?.internalPartNo || "N/A"}</td>
                    <td>{partDetails?.partName || "N/A"}</td>
                    <td>{partDetails?.modelId?.name || "N/A"}</td>
                    <td>{partDetails?.categoryId?.name || "N/A"}</td>
                    <td className="grn-right-align">
                      {item.qty}({partDetails.uomId.name})
                    </td>
                    <td className="grn-right-align">{item.partId?.unitPrice || "N/A"}</td>
                    <td>
                      <div className="action-buttons">
                        {permission.menus.grns.update ?
                          <button
                            className="edit-btn"
                            onClick={() => handleEdit(item)}
                          >
                            <Edit size={16} />
                          </button> : <></>}
                        {permission.menus.grns.delete ?
                          <button
                            className="delete-btn"
                            onClick={() => confirmDelete(item._id || "")}
                          >
                            <Trash size={16} />
                          </button>
                          : <></>}
                      </div>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={8} style={{ textAlign: "center" }}>
                  No GRNs added
                </td>
              </tr>
            )}
          </tbody>
        </table>

        {tableData.length > 0 && (
          <button
            className="grn-complete-button"
            onClick={handleComplete}
            disabled={isEditing}
          >
            Complete
          </button>
        )}
      </div>

      <PopupConfirmation
        isOpen={showPopup}
        message={popupMessage}
        type={popupType}
        onClose={() => setShowPopup(false)}
        autoCloseTime={3000}
        onConfirm={isConfirmation ? handleDelete : undefined}
        isConfirmation={isConfirmation}
      />
    </div>
  );
};

export default NewGRN;